import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../lib/supabaseClient';
import BarRestaurantOrderCard from './BarRestaurantOrderCard';
import BarRestaurantOrderDetailModal from './BarRestaurantOrderDetailModal';
import BarRestaurantListFullscreen from './BarRestaurantListFullscreen';
import BarRestaurantListFullscreenCustom from './BarRestaurantListFullscreenCustom';
import LoadingSpinner from './LoadingSpinner';
import './BarRestaurantOrderKanban.css';
import AppIcon from './icons/AppIcon';

// Componente SortableOrderCard para drag and drop
function SortableOrderCard({ order, onOpenCard, isDragging }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: order.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="order-item"
    >
      <BarRestaurantOrderCard order={order} onOpenCard={onOpenCard} isDragging={isDragging} />
    </div>
  );
}

// Componente DroppableColumn para tornar a coluna uma zona de drop
function DroppableColumn({ columnId, children }) {
  const { setNodeRef } = useDroppable({
    id: columnId,
  });

  return (
    <div ref={setNodeRef} className="orders-list">
      {children}
    </div>
  );
}

export default function BarRestaurantOrderKanban({ projectId, onBack }) {
  const [project, setProject] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [minimizedColumns, setMinimizedColumns] = useState({});
  const [activeOrder, setActiveOrder] = useState(null);
  const [fullscreenListId, setFullscreenListId] = useState(null);
  const [fullscreenCustomMode, setFullscreenCustomMode] = useState(false);
  const [activeTurno, setActiveTurno] = useState(null);

  // Configurar sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Carregar projeto
  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      loadTurnoAtivo();
    }
  }, [projectId, activeTurno?.id]);

  // Configurar Real-time para pedidos
  useEffect(() => {
    if (!projectId || !activeTurno?.id) return;

    console.log('[Bar Restaurant Order Kanban] Configurando Real-time para pedidos');

    const ordersChannel = supabase
      .channel(`bar_restaurant_orders:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bar_restaurant_orders',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          console.log('[Bar Restaurant Order Kanban] 🔴 Real-time - Pedido alterado:', payload);
          loadOrders(activeTurno.id);
        }
      )
      .subscribe();

    // Cleanup ao desmontar
    return () => {
      console.log('[Bar Restaurant Order Kanban] Desconectando Real-time');
      supabase.removeChannel(ordersChannel);
    };
  }, [projectId]);

  useEffect(() => {
    const handleTurnoUpdate = () => {
      loadTurnoAtivo();
    };

    window.addEventListener('bar-restaurant-turno-updated', handleTurnoUpdate);
    return () => window.removeEventListener('bar-restaurant-turno-updated', handleTurnoUpdate);
  }, []);

  const loadProject = async () => {
    try {
      console.log('[BarRestaurantOrderKanban] Carregando projeto:', projectId);
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      console.log('[BarRestaurantOrderKanban] Projeto carregado:', data.name);
      setProject(data);
    } catch (error) {
      console.error('[BarRestaurantOrderKanban] Erro ao carregar projeto:', error);
    }
  };

  const loadTurnoAtivo = async () => {
    try {
      const response = await fetch(`/api/bar-restaurant/turnos/active/${projectId}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao carregar turno');
      }

      setActiveTurno(data.turno);
      loadOrders(data.turno?.id || null);
    } catch (error) {
      console.error('[BarRestaurantOrderKanban] Erro ao carregar turno:', error);
    }
  };

  const loadOrders = async (turnoId = activeTurno?.id) => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      if (!turnoId) {
        setOrders([]);
        return;
      }

      const { data, error } = await supabase
        .from('bar_restaurant_orders')
        .select('*')
        .eq('project_id', projectId)
        .eq('company_id', user.company_id)
        .eq('turno_id', turnoId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const excludedStatuses = ['arquivado', 'cancelado'];
      const filteredOrders = (data || []).filter(order => !excludedStatuses.includes(order.status_atual));
      setOrders(filteredOrders);
    } catch (error) {
      console.error('[Bar Restaurant Order Kanban] Erro ao carregar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleCloseModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
    loadOrders();
  };

  const toggleColumnMinimized = (columnName) => {
    setMinimizedColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const draggedOrder = orders.find(o => o.id === active.id);
    if (draggedOrder) {
      setActiveOrder(draggedOrder);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveOrder(null);

    if (!over) {
      console.warn('⚠️ over é null');
      return;
    }

    const orderId = active.id;
    const overItemId = over.id;

    console.log('🔍 Drag End - orderId:', orderId, 'overItemId:', overItemId);

    if (!orderId || !overItemId) return;

    try {
      // Mapear coluna para status
      const columnToStatus = {
        'pedidos_em_aberto': 'pedidos_em_aberto',
        'em_producao': 'em_producao',
        'pronto': 'pronto',
        'entregue': 'entregue'
      };

      // Detectar qual coluna o card foi solto
      let targetColumnId = null;

      if (columnToStatus[overItemId]) {
        // over.id é uma coluna
        console.log('📍 Solto diretamente em coluna:', overItemId);
        targetColumnId = overItemId;
      } else {
        // over.id é um card, encontre sua coluna
        console.log('📍 Solto sobre card:', overItemId);
        const overOrder = orders.find(o => o.id === overItemId);
        if (overOrder) {
          const statusToColumn = {
            'pedidos_em_aberto': 'pedidos_em_aberto',
            'em_producao': 'em_producao',
            'pronto': 'pronto',
            'entregue': 'entregue'
          };
          targetColumnId = statusToColumn[overOrder.status_atual];
          console.log('📍 Coluna detectada pelo card:', targetColumnId);
        }
      }

      if (!targetColumnId) {
        console.warn('⚠️ Coluna de destino não detectada. overItemId:', overItemId);
        console.log('📋 Colunas disponíveis:', Object.keys(columnToStatus));
        console.log('📋 Pedidos:', orders.map(o => ({ id: o.id, status: o.status_atual })));
        return;
      }

      const novoStatus = columnToStatus[targetColumnId];
      if (!novoStatus) {
        console.warn('⚠️ Status não mapeado para coluna:', targetColumnId);
        return;
      }

      const agora = new Date().toISOString();

      // Buscar pedido atual
      const { data: orderData } = await supabase
        .from('bar_restaurant_orders')
        .select('*')
        .eq('id', orderId)
        .single();

      if (!orderData) {
        console.warn('⚠️ Pedido não encontrado:', orderId);
        return;
      }

      console.log('📦 Pedido atual:', { id: orderData.id, status: orderData.status_atual, novoStatus });

      // Se o status mudou
      if (orderData.status_atual !== novoStatus) {
        // Atualizar histórico
        const historicoAtual = orderData.historico_movimentacoes || [];
        const novoHistorico = [
          ...historicoAtual,
          {
            lista: Object.keys(columnToStatus).find(k => columnToStatus[k] === novoStatus),
            timestamp: agora,
            acao: 'movido'
          }
        ];

        // Atualizar pedido no banco
        const { error } = await supabase
          .from('bar_restaurant_orders')
          .update({
            status_atual: novoStatus,
            timestamp_entrada_lista: agora,
            historico_movimentacoes: novoHistorico
          })
          .eq('id', orderId);

        if (error) {
          console.error('❌ Erro ao atualizar no banco:', error);
          return;
        }

        console.log('✅ Pedido movido para novo status:', novoStatus);

        // Atualizar apenas o estado local sem recarregar
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === orderId
              ? {
                  ...order,
                  status_atual: novoStatus,
                  timestamp_entrada_lista: agora,
                  historico_movimentacoes: novoHistorico
                }
              : order
          )
        );
      } else {
        console.log('ℹ️ Status não mudou, card solto na mesma coluna');
      }
    } catch (error) {
      console.error('❌ Erro ao mover pedido:', error);
    }
  };

  if (!project) {
    return (
      <div className="kanban-container">
        <LoadingSpinner message="Carregando projeto..." />
      </div>
    );
  }

  // Agrupar pedidos por status (baseado nas listas do projeto)
  const pedidosEmAberto = orders.filter(o => o.status_atual === 'pedidos_em_aberto');
  const pedidosEmProducao = orders.filter(o => o.status_atual === 'em_producao');
  const pedidosPronto = orders.filter(o => o.status_atual === 'pronto');
  const pedidosEntregue = orders.filter(o => o.status_atual === 'entregue');

  // Renderizar fullscreen personalizado se ativado
  if (fullscreenCustomMode) {
    return (
      <BarRestaurantListFullscreenCustom
        onClose={() => setFullscreenCustomMode(false)}
        projectId={projectId}
        allOrders={orders}
      />
    );
  }

  // Renderizar fullscreen se ativado
  if (fullscreenListId) {
    const listNames = {
      'pedidos_em_aberto': 'Pedidos em aberto',
      'em_producao': 'Em produção',
      'pronto': 'Pronto',
      'entregue': 'Entregue'
    };

    const listOrders = {
      'pedidos_em_aberto': pedidosEmAberto,
      'em_producao': pedidosEmProducao,
      'pronto': pedidosPronto,
      'entregue': pedidosEntregue
    };

    return (
      <BarRestaurantListFullscreen
        listId={fullscreenListId}
        listName={listNames[fullscreenListId]}
        orders={listOrders[fullscreenListId]}
        onClose={() => setFullscreenListId(null)}
        projectId={projectId}
      />
    );
  }

  return (
    <div className="kanban-container">
      {/* Header */}
      <div className="kanban-header">
        <h1>{project?.name || 'Kanban'}</h1>
        <div className="kanban-header-actions">
          <button
            className="btn-custom-fullscreen"
            onClick={() => setFullscreenCustomMode(true)}
            title="Tela Cheia Personalizada"
          >
            <AppIcon name="dashboard_customize" />
            <span>Tela Cheia Personalizada</span>
          </button>
          <button
            className="btn-back"
            onClick={onBack}
            title="Voltar"
          >
            <AppIcon name="arrow_back" />
          </button>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="order-kanban-content">
        {loading ? (
          <LoadingSpinner message="Carregando pedidos..." />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={[
                'pedidos_em_aberto',
                'em_producao',
                'pronto',
                'entregue'
              ]}
              strategy={horizontalListSortingStrategy}
            >
              <div className="order-kanban-columns">
                {/* Coluna: Pedidos em aberto */}
                <div className={`order-kanban-column ${minimizedColumns['pedidos_em_aberto'] ? 'minimized' : ''}`}>
                  <div className="column-header">
                    <div className="column-title-section">
                      <h2>Pedidos em aberto</h2>
                      <span className="column-count">{pedidosEmAberto.length}</span>
                    </div>
                    <div className="column-actions">
                      <button
                        className="btn-fullscreen"
                        onClick={() => setFullscreenListId('pedidos_em_aberto')}
                        title="Tela Cheia"
                      >
                        <AppIcon name="fullscreen" />
                      </button>
                      <button
                        className="btn-minimize"
                        onClick={() => toggleColumnMinimized('pedidos_em_aberto')}
                        title={minimizedColumns['pedidos_em_aberto'] ? 'Expandir' : 'Minimizar'}
                      >
                        <AppIcon name={minimizedColumns['pedidos_em_aberto'] ? 'unfold_more' : 'unfold_less'} />
                      </button>
                    </div>
                  </div>
                  <SortableContext
                    items={pedidosEmAberto.map(o => o.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <DroppableColumn columnId="pedidos_em_aberto">
                      {pedidosEmAberto.length === 0 ? (
                        <div className="empty-state">
                          <AppIcon name="receipt_long" />
                          <p>Nenhum pedido em aberto</p>
                        </div>
                      ) : (
                        pedidosEmAberto.map(order => (
                          <SortableOrderCard
                            key={order.id}
                            order={order}
                            onOpenCard={handleOpenOrder}
                            isDragging={activeOrder?.id === order.id}
                          />
                        ))
                      )}
                    </DroppableColumn>
                  </SortableContext>
                </div>

                {/* Coluna: Em produção */}
                <div className={`order-kanban-column ${minimizedColumns['em_producao'] ? 'minimized' : ''}`}>
                  <div className="column-header">
                    <div className="column-title-section">
                      <h2>Em produção</h2>
                      <span className="column-count">{pedidosEmProducao.length}</span>
                    </div>
                    <div className="column-actions">
                      <button
                        className="btn-fullscreen"
                        onClick={() => setFullscreenListId('em_producao')}
                        title="Tela Cheia"
                      >
                        <AppIcon name="fullscreen" />
                      </button>
                      <button
                        className="btn-minimize"
                        onClick={() => toggleColumnMinimized('em_producao')}
                        title={minimizedColumns['em_producao'] ? 'Expandir' : 'Minimizar'}
                      >
                        <AppIcon name={minimizedColumns['em_producao'] ? 'unfold_more' : 'unfold_less'} />
                      </button>
                    </div>
                  </div>
                  <SortableContext
                    items={pedidosEmProducao.map(o => o.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <DroppableColumn columnId="em_producao">
                      {pedidosEmProducao.length === 0 ? (
                        <div className="empty-state">
                          <AppIcon name="receipt_long" />
                          <p>Nenhum pedido em produção</p>
                        </div>
                      ) : (
                        pedidosEmProducao.map(order => (
                          <SortableOrderCard
                            key={order.id}
                            order={order}
                            onOpenCard={handleOpenOrder}
                            isDragging={activeOrder?.id === order.id}
                          />
                        ))
                      )}
                    </DroppableColumn>
                  </SortableContext>
                </div>

                {/* Coluna: Pronto */}
                <div className={`order-kanban-column ${minimizedColumns['pronto'] ? 'minimized' : ''}`}>
                  <div className="column-header">
                    <div className="column-title-section">
                      <h2>Pronto</h2>
                      <span className="column-count">{pedidosPronto.length}</span>
                    </div>
                    <div className="column-actions">
                      <button
                        className="btn-fullscreen"
                        onClick={() => setFullscreenListId('pronto')}
                        title="Tela Cheia"
                      >
                        <AppIcon name="fullscreen" />
                      </button>
                      <button
                        className="btn-minimize"
                        onClick={() => toggleColumnMinimized('pronto')}
                        title={minimizedColumns['pronto'] ? 'Expandir' : 'Minimizar'}
                      >
                        <AppIcon name={minimizedColumns['pronto'] ? 'unfold_more' : 'unfold_less'} />
                      </button>
                    </div>
                  </div>
                  <SortableContext
                    items={pedidosPronto.map(o => o.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <DroppableColumn columnId="pronto">
                      {pedidosPronto.length === 0 ? (
                        <div className="empty-state">
                          <AppIcon name="receipt_long" />
                          <p>Nenhum pedido pronto</p>
                        </div>
                      ) : (
                        pedidosPronto.map(order => (
                          <SortableOrderCard
                            key={order.id}
                            order={order}
                            onOpenCard={handleOpenOrder}
                            isDragging={activeOrder?.id === order.id}
                          />
                        ))
                      )}
                    </DroppableColumn>
                  </SortableContext>
                </div>

                {/* Coluna: Entregue */}
                <div className={`order-kanban-column ${minimizedColumns['entregue'] ? 'minimized' : ''}`}>
                  <div className="column-header">
                    <div className="column-title-section">
                      <h2>Entregue</h2>
                      <span className="column-count">{pedidosEntregue.length}</span>
                    </div>
                    <div className="column-actions">
                      <button
                        className="btn-fullscreen"
                        onClick={() => setFullscreenListId('entregue')}
                        title="Tela Cheia"
                      >
                        <AppIcon name="fullscreen" />
                      </button>
                      <button
                        className="btn-minimize"
                        onClick={() => toggleColumnMinimized('entregue')}
                        title={minimizedColumns['entregue'] ? 'Expandir' : 'Minimizar'}
                      >
                        <AppIcon name={minimizedColumns['entregue'] ? 'unfold_more' : 'unfold_less'} />
                      </button>
                    </div>
                  </div>
                  <SortableContext
                    items={pedidosEntregue.map(o => o.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <DroppableColumn columnId="entregue">
                      {pedidosEntregue.length === 0 ? (
                        <div className="empty-state">
                          <AppIcon name="receipt_long" />
                          <p>Nenhum pedido entregue</p>
                        </div>
                      ) : (
                        pedidosEntregue.map(order => (
                          <SortableOrderCard
                            key={order.id}
                            order={order}
                            onOpenCard={handleOpenOrder}
                            isDragging={activeOrder?.id === order.id}
                          />
                        ))
                      )}
                    </DroppableColumn>
                  </SortableContext>
                </div>
              </div>
            </SortableContext>

            <DragOverlay>
              {activeOrder ? (
                <div className="bar-restaurant-order-card dragging">
                  <div className="order-header">
                    <div className="order-mesa">
                      <AppIcon name="table_restaurant" />
                      <span className="mesa-number">Mesa {activeOrder.order_payload?.mesa || '?'}</span>
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Modal de Detalhes do Pedido */}
      {showOrderModal && selectedOrder && (
        <BarRestaurantOrderDetailModal
          order={selectedOrder}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
